// ScopeTree (knowledge/08, cat 48.8–48.10) — the explorable scope tree in the sidebar's SCOPE
// PATH region. Replaces the linear spine (ancestors + current-node children) with a real tree:
// SIBLINGS visible at every level, the active descendant path expanded from the URL, a muted TYPE CHIP
// per row (CLIENT / ENGMT / PROJ).
//
// Locked mechanics (T1–T3):
//  - chevron = expand/collapse ONLY; descendant labels navigate to their node's home.
//    Workspace headers open their home and expand on first click; the next click only collapses.
//  - workspace groups default closed, including the active workspace on a deep link. Descendants
//    use URL-derived expansion. Session-ephemeral manual toggles override either default.
//    Opening a workspace closes the other workspace groups without resetting their descendants.
//  - full lazy tree: children render on expand from the ALREADY-FETCHED scope arrays in
//    ContextChain — no caps, no new fetches, no backend change. The rail scrolls.
//
// Invariants: workspace roots can be included as collapsible headers; client-less engagements
// (cross-client hosts, A5/A6) are workspace-level rows; apps NEVER appear here (A7).
import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Folder } from "lucide-react";
import { clsx } from "clsx";
import { nodeAncestry, useContextChain } from "../state/ContextChain";
import type { ScopeNodeResp } from "../api/scopeTree";
import { navTo } from "./ContextChain";
import { WorkspaceIcon } from "./WorkspaceIcon";
import { ClientLogo } from "./ClientLogo";
import { EngagementIcon } from "./EngagementIcon";

type NodeType = "workspace" | "client" | "engagement" | "project";

// Single-letter type chips (operator amendment 2026-07-02): C / E / P — full word on hover.
const TYPE_CHIP: Record<NodeType, string> = {
  workspace: "W",
  client: "C",
  engagement: "E",
  project: "P",
};
const TYPE_NAME: Record<NodeType, string> = {
  workspace: "Workspace",
  client: "Client",
  engagement: "Engagement",
  project: "Project",
};

interface TreeNode {
  id: string;
  label: string;
  type: NodeType;
  isCurrent: boolean;
  isActiveWorkspace: boolean;
  onNavigate: () => void;
  children: TreeNode[];
}

function TreeRow({ node, depth, expanded, onToggle, currentStyle = "bar" }: {
  node: TreeNode;
  depth: number;
  expanded: (id: string) => boolean;
  onToggle: (id: string) => void;
  /** How the CURRENT node is emphasized: "bar" (accent bar+canvas bg, tree mode) or "text"
   *  (accessible accent tint — lineage mode while an app is open, where the active screen owns
   *  the accent bar). */
  currentStyle?: "bar" | "text";
}): React.ReactElement {
  const isOpen = expanded(node.id);
  const hasChildren = node.children.length > 0;
  const isWorkspace = node.type === "workspace";
  const canToggle = hasChildren || isWorkspace;
  const icon =
    node.type === "workspace" ? <WorkspaceIcon workspaceId={node.id} label={node.label} size={18} />
      : node.type === "client" ? <ClientLogo clientId={node.id} name={node.label} size={15} />
      : node.type === "engagement" ? <EngagementIcon name={node.label} size={15} />
        : <Folder size={15} aria-hidden />;
  return (
    <>
      <div
        className={clsx(
          // min-h-11 (44px, row 5.5/WCAG 2.5.5) — no locked visual-height grammar on this row
          // (unlike SideNav's NavRow), so a direct bump is the lower-risk fix here.
          "flex min-h-11 w-full items-center border-l-2 pr-2 transition-colors",
          // Active bg uses the theme's content canvas color, while semantic text and hover
          // tokens retain contrast on either shell frame.
          node.isCurrent && currentStyle === "bar"
            ? "border-[var(--color-orange-55)] bg-[var(--color-surface-base)] text-[var(--color-text-primary)]"
            : node.isCurrent
              ? "border-transparent bg-[var(--color-accent-subtle)] text-[var(--color-text-primary)] font-medium"
              : "border-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-subtle)] hover:text-[var(--color-text-primary)]",
        )}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
      >
        {/* chevron = expand/collapse ONLY (T1) — never navigates; leaf rows keep the gutter */}
        {canToggle ? (
          <button
            type="button"
            aria-label={`${isOpen ? "Collapse" : "Expand"} ${node.label}`}
            aria-expanded={isOpen}
            onClick={() => onToggle(node.id)}
            className="flex h-5 w-5 shrink-0 items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            {isOpen ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
          </button>
        ) : (
          <span className="h-5 w-5 shrink-0" aria-hidden />
        )}
        {/* Workspace headers toggle; collapsing never navigates away from the current screen. */}
        <button
          type="button"
          onClick={() => {
            if (isWorkspace) {
              onToggle(node.id);
              if (!isOpen) node.onNavigate();
            } else {
              node.onNavigate();
            }
          }}
          aria-expanded={isWorkspace ? isOpen : undefined}
          aria-current={node.isCurrent ? "page" : undefined}
          // self-stretch: fills the row's full min-h-11 (the parent's items-center would
          // otherwise size this to its own (shorter) content height).
          className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left"
        >
          <span
            className={clsx(
              "flex shrink-0 items-center justify-center",
              node.isActiveWorkspace && "text-[var(--color-orange-55)]",
            )}
          >
            {icon}
          </span>
          <span
            className={clsx(
              "min-w-0 flex-1 truncate text-[13px]",
              node.isActiveWorkspace && "font-semibold text-[var(--color-text-primary)]",
            )}
          >
            {node.label}
          </span>
        </button>
        {/* type chip (T2, amended: single letter) — muted, right-aligned; full name on hover */}
        {node.type !== "workspace" && (
          <span
            title={TYPE_NAME[node.type]}
            className="ml-1 shrink-0 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]"
          >
            {TYPE_CHIP[node.type]}
          </span>
        )}
      </div>
      {/* lazy render (T3): children mount only while expanded */}
      {hasChildren && isOpen &&
        node.children.map((c) => (
          <TreeRow key={c.id} node={c} depth={depth + 1} expanded={expanded} onToggle={onToggle} />
        ))}
    </>
  );
}

export function ScopeTree({
  lineageOnly = false,
  includeWorkspaces = false,
}: {
  lineageOnly?: boolean;
  includeWorkspaces?: boolean;
} = {}): React.ReactElement | null {
  const { scopeNodes, workspaces, workspaceId, clientId, engagementId, projectId } = useContextChain();
  const navigate = useNavigate();
  const { node: nodeParam } = useParams({ strict: false }) as { node?: string };

  // A generic route's node is authoritative. Scalar precedence can otherwise select a free-placed
  // node's typed host ancestor (for example, the project above a client) instead of the page itself.
  const currentId = nodeParam || projectId || engagementId || clientId || workspaceId;

  // Session-ephemeral manual overrides (T1): per-node explicit open/close beats the URL default.
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(new Map());
  // Roots are deliberately closed on first render; navigating must not force them open again.
  // Within an opened root, preserve URL-derived descendant expansion (including free placements).
  const urlPath = new Set([currentId, ...nodeAncestry(currentId, scopeNodes).map((a) => a.id)]);
  const workspaceIds = new Set([
    ...(workspaces ?? []).map((workspace) => workspace.id),
    ...scopeNodes.filter((node) => node.node_type === "workspace").map((node) => node.id),
  ]);
  const defaultExpanded = (id: string): boolean => !workspaceIds.has(id) && urlPath.has(id);
  const expanded = (id: string): boolean => overrides.get(id) ?? defaultExpanded(id);
  const onToggle = (id: string): void =>
    setOverrides((prev) => {
      const next = new Map(prev);
      const opening = !(prev.get(id) ?? defaultExpanded(id));
      if (opening && workspaceIds.has(id)) {
        for (const workspaceId of workspaceIds) next.set(workspaceId, false);
      }
      next.set(id, opening);
      return next;
    });

  const isTreeType = (t: string): t is NodeType =>
    t === "workspace" || t === "client" || t === "engagement" || t === "project";
  const isLineageType = (t: string): t is Exclude<NodeType, "workspace"> =>
    t === "client" || t === "engagement" || t === "project";
  // Children-by-parent from the NODE GRAPH (§6): ANY tree-type node under ANY node, generically — so
  // free shapes (client under engagement, project under client, …) render in their TRUE position.
  // Apps never appear (A7); firm/workspace are not tree rows (workspace = the switcher header).
  const childrenOf = (parentId: string): ScopeNodeResp[] =>
    scopeNodes.filter((n) => n.parent_id === parentId && isTreeType(n.node_type));

  // Navigation goes through the GENERIC /w/$ws/n/$node route (§6, R2) — a node addressed by id, so a
  // free-placed node gets a lossless URL. useUrlScopeSync derives the breadcrumb/tree scope from the
  // node's ancestry on arrival. The workspace anchor comes from the node's own ancestry (parent-walk).
  const navFor = (n: ScopeNodeResp): (() => void) => {
    if (n.node_type === "workspace") {
      return () => navigate(navTo.workspaceHome(n.id) as never);
    }
    const ws = nodeAncestry(n.id, scopeNodes).find((a) => a.node_type === "workspace")?.id ?? workspaceId;
    return () => navigate(navTo.nodeHome(ws, n.id) as never);
  };

  const buildNode = (n: ScopeNodeResp): TreeNode => ({
    id: n.id,
    label: n.name,
    type: n.node_type as NodeType,
    isCurrent: n.id === currentId,
    isActiveWorkspace: n.node_type === "workspace" && n.id === workspaceId,
    onNavigate: navFor(n),
    children: childrenOf(n.id).map(buildNode),
  });

  // Lineage-only mode (48.15, amendment #4): while an app is open, the tree collapses to the ANCESTRY
  // SPINE of the current node — no siblings/children/chevrons; chips kept; label click = jump up.
  if (lineageOnly) {
    const self = scopeNodes.find((n) => n.id === currentId && isLineageType(n.node_type));
    const spineNodes: ScopeNodeResp[] = [
      ...nodeAncestry(currentId, scopeNodes)
        .filter((a) => isLineageType(a.node_type))
        .reverse()
        .map((a) => scopeNodes.find((n) => n.id === a.id))
        .filter((n): n is ScopeNodeResp => Boolean(n)),
      ...(self ? [self] : []),
    ];
    if (spineNodes.length === 0) return null; // workspace-level app: no lineage below the switcher
    const spine = spineNodes.map((n) => ({ ...buildNode(n), children: [] as TreeNode[] }));
    return (
      <div data-app="scope-tree" data-mode="lineage">
        {spine.map((n, i) => (
          // 48.21-A: current lineage node = accessible accent tint; the active screen owns the bar.
          <TreeRow key={n.id} node={n} depth={i} expanded={expanded} onToggle={onToggle} currentStyle="text" />
        ))}
      </div>
    );
  }

  const workspaceRoots = includeWorkspaces ? workspaces.map((workspace) => {
    const node = scopeNodes.find(
      (candidate) => candidate.id === workspace.id && candidate.node_type === "workspace",
    ) ?? {
      id: workspace.id,
      node_type: "workspace" as const,
      parent_id: null,
      path: `/${workspace.id}/`,
      name: workspace.label,
      is_barrier: false,
    };
    return buildNode(node);
  }) : [];
  const roots: TreeNode[] = includeWorkspaces
    ? workspaceRoots
    : childrenOf(workspaceId).filter((node) => node.node_type !== "workspace").map(buildNode);
  if (roots.length === 0) return null;
  return (
    <div data-app="scope-tree">
      {roots.map((r) => (
        <TreeRow key={r.id} node={r} depth={0} expanded={expanded} onToggle={onToggle} />
      ))}
    </div>
  );
}
